import React, { useCallback, useEffect, useState } from 'react';
import {
  Form,
  Input,
  Button,
  Steps,
  Alert,
  Collapse,
  List,
  Typography,
  Spin,
  Breadcrumb,
  message,
} from 'antd';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import {
  CheckCircleOutlined,
  SendOutlined,
} from '@ant-design/icons';
import {
  getBot,
  getBotStatus,
  getBotLogs,
  telegramLoginStart,
  telegramLoginCode,
  telegramLogin2FA,
} from '../../services/api';
import '../AddAssistant.css';
import './AddBot.css';

const { Title, Paragraph, Text } = Typography;

function BotConnectTelegram() {
  const { botId } = useParams();
  const navigate = useNavigate();
  const { keycloak, initialized } = useKeycloak();

  const [bot, setBot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runtimeOffline, setRuntimeOffline] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loginStep, setLoginStep] = useState(0);
  const [loginPhone, setLoginPhone] = useState('');
  const [loginCode, setLoginCode] = useState('');
  const [login2FA, setLogin2FA] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [needs2FA, setNeeds2FA] = useState(false);
  const [connected, setConnected] = useState(false);

  const loadBot = useCallback(async () => {
    if (!keycloak?.token) return;
    setLoading(true);
    try {
      const data = await getBot(keycloak, botId);
      setBot(data);
      setConnected(Boolean(data.has_credentials));
      if (data.has_credentials) {
        setLoginStep(2);
      }
    } catch (e) {
      message.error(e.message || 'Failed to load bot');
    } finally {
      setLoading(false);
    }
  }, [keycloak, botId]);

  const refreshStatus = useCallback(async () => {
    if (!keycloak?.token) return;
    try {
      const [st, lg] = await Promise.all([
        getBotStatus(keycloak, botId),
        getBotLogs(keycloak, botId),
      ]);
      setRuntimeOffline(false);
      setLogs(lg?.logs || []);
      if (st?.has_credentials) {
        setConnected(true);
      }
    } catch (e) {
      const msg = e.message || '';
      if (msg.includes('runtime') || msg.includes('503')) {
        setRuntimeOffline(true);
      }
    }
  }, [keycloak, botId]);

  useEffect(() => {
    if (!initialized) return;
    loadBot();
    refreshStatus();
  }, [initialized, loadBot, refreshStatus]);

  const handleLoginStart = async () => {
    if (!loginPhone.trim()) {
      message.error('Enter phone number with country code');
      return;
    }
    setLoginLoading(true);
    try {
      await telegramLoginStart(keycloak, botId, loginPhone.trim());
      setLoginStep(1);
      message.success('Code sent to Telegram');
      setRuntimeOffline(false);
    } catch (e) {
      const msg = e.message || 'Login failed';
      if (msg.includes('runtime') || msg.includes('503')) {
        setRuntimeOffline(true);
      }
      message.error(msg);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLoginCode = async () => {
    if (!loginCode.trim()) {
      message.error('Enter the code');
      return;
    }
    setLoginLoading(true);
    try {
      const result = await telegramLoginCode(keycloak, botId, loginCode.trim());
      if (result.needs_2fa) {
        setNeeds2FA(true);
        setLoginStep(2);
      } else {
        setConnected(true);
        message.success('Telegram account linked');
        await loadBot();
      }
    } catch (e) {
      message.error(e.message || 'Invalid code');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogin2FA = async () => {
    if (!login2FA) {
      message.error('Enter 2FA password');
      return;
    }
    setLoginLoading(true);
    try {
      await telegramLogin2FA(keycloak, botId, login2FA);
      setConnected(true);
      message.success('Telegram account linked');
      await loadBot();
    } catch (e) {
      message.error(e.message || '2FA failed');
    } finally {
      setLoginLoading(false);
    }
  };

  const stepItems = [
    { title: 'Configure' },
    { title: 'Connect' },
    { title: 'Done' },
  ];
  const currentStep = connected ? 2 : 1;

  return (
    <div className="page-container add-assistant-page add-bot-page">
      <Breadcrumb
        className="add-assistant-breadcrumb"
        items={[
          { title: <Link to="/channels/bots">Bots</Link> },
          { title: bot?.name || 'Bot' },
          { title: 'Connect Telegram' },
        ]}
      />

      <div className="collections-page-header add-assistant-page-header">
        <div className="page-header" style={{ flex: 1, minWidth: 0 }}>
          <h2 className="page-header-title">Connect Telegram</h2>
          <span className="page-header-subtitle">
            Link the Telegram account that will send and receive messages for this bot
          </span>
        </div>
      </div>

      <Steps current={currentStep} items={stepItems} style={{ maxWidth: 560, margin: '0 auto 24px' }} />

      <div className="add-assistant-form-panel collections-table-wrapper">
        <Spin spinning={loading}>
          <div className="add-assistant-form-inner bot-connect-panel">
            {runtimeOffline && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message="Messaging service is offline"
                description="Ensure the bot-runtime service is running, then try again."
              />
            )}

            {connected ? (
              <div className="bot-connect-success">
                <CheckCircleOutlined style={{ fontSize: 48, color: 'var(--color-success, #52c41a)' }} />
                <Title level={4} style={{ marginTop: 16 }}>Telegram linked</Title>
                <Paragraph type="secondary">
                  {bot?.name} is ready. Enable the bot from the list or edit settings anytime.
                </Paragraph>
                <div className="add-assistant-form-footer-actions" style={{ marginTop: 24 }}>
                  <Button type="primary" onClick={() => navigate('/channels/bots')}>
                    Go to bots
                  </Button>
                  <Button style={{ marginLeft: 8 }} onClick={() => navigate(`/channels/bots/${botId}/edit`)}>
                    Edit settings
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="bot-connect-intro">
                  <SendOutlined style={{ fontSize: 28, color: 'var(--color-primary)' }} />
                  <Title level={5} style={{ marginTop: 12, marginBottom: 4 }}>
                    Sign in with Telegram
                  </Title>
                  <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                    Use the phone number of the account that should participate in chats.
                  </Paragraph>
                </div>

                {loginStep === 0 && (
                  <Form layout="vertical" style={{ marginTop: 24 }}>
                    <Form.Item label="Phone number" required>
                      <Input
                        placeholder="+1234567890"
                        value={loginPhone}
                        onChange={(e) => setLoginPhone(e.target.value)}
                        size="large"
                      />
                    </Form.Item>
                    <Button
                      type="primary"
                      size="large"
                      loading={loginLoading}
                      onClick={handleLoginStart}
                      block
                    >
                      Send code
                    </Button>
                  </Form>
                )}

                {loginStep === 1 && !needs2FA && (
                  <Form layout="vertical" style={{ marginTop: 24 }}>
                    <Form.Item label="Verification code" required>
                      <Input
                        placeholder="12345"
                        value={loginCode}
                        onChange={(e) => setLoginCode(e.target.value)}
                        size="large"
                      />
                    </Form.Item>
                    <Button
                      type="primary"
                      size="large"
                      loading={loginLoading}
                      onClick={handleLoginCode}
                      block
                    >
                      Verify
                    </Button>
                    <Button type="link" block onClick={() => setLoginStep(0)} style={{ marginTop: 8 }}>
                      Use a different number
                    </Button>
                  </Form>
                )}

                {loginStep === 2 && needs2FA && !connected && (
                  <Form layout="vertical" style={{ marginTop: 24 }}>
                    <Form.Item label="Two-step password" required>
                      <Input.Password
                        placeholder="2FA password"
                        value={login2FA}
                        onChange={(e) => setLogin2FA(e.target.value)}
                        size="large"
                      />
                    </Form.Item>
                    <Button
                      type="primary"
                      size="large"
                      loading={loginLoading}
                      onClick={handleLogin2FA}
                      block
                    >
                      Complete sign-in
                    </Button>
                  </Form>
                )}
              </>
            )}

            {logs.length > 0 && (
              <Collapse
                ghost
                style={{ marginTop: 24 }}
                items={[
                  {
                    key: 'activity',
                    label: 'Activity',
                    children: (
                      <List
                        size="small"
                        dataSource={logs.slice(0, 15)}
                        renderItem={(item) => <List.Item><Text type="secondary">{item}</Text></List.Item>}
                      />
                    ),
                  },
                ]}
              />
            )}
          </div>
        </Spin>
      </div>
    </div>
  );
}

export default BotConnectTelegram;
